import { ASSET_CANVAS_SQUARE_NODE_SIZE } from '@/lib/canvas/asset-canvas-node-size';
import {
  STORYBOARD_AUDIO_COLLECTION_NODE_FALLBACK_HEIGHT,
  STORYBOARD_AUDIO_COLLECTION_NODE_WIDTH,
  STORYBOARD_AUDIO_ITEM_WIDTH,
} from '@/lib/canvas/storyboard-canvas-layout';

interface CanvasNodeWithSize {
  position: { x: number; y: number };
  measured?: {
    width?: number;
    height?: number;
  };
  width?: number;
  height?: number;
}

export function isAssetCanvasAutoFocusNodeType(type: string | undefined): boolean {
  return type === 'assetTagImage';
}

export function isStoryboardCanvasAutoFocusNodeType(type: string | undefined): boolean {
  return (
    type === 'storyboardPlanImage' ||
    type === 'storyboardPlanVideo' ||
    isStoryboardCanvasAudioCollectionNodeType(type)
  );
}

export function isStoryboardCanvasAudioCollectionNodeType(type: string | undefined): boolean {
  return (
    type === 'storyboardDialogueGroup' ||
    type === 'storyboardVoiceoverGroup' ||
    type === 'storyboardSoundEffectGroup' ||
    type === 'storyboardDialogueItem' ||
    type === 'storyboardVoiceoverItem' ||
    type === 'storyboardSoundEffectItem'
  );
}

export function getStoryboardCanvasNodeFocusFallbackSize(type: string | undefined): {
  width: number;
  height: number;
} {
  if (isStoryboardCanvasAudioCollectionNodeType(type)) {
    return {
      width: STORYBOARD_AUDIO_COLLECTION_NODE_WIDTH,
      height: STORYBOARD_AUDIO_COLLECTION_NODE_FALLBACK_HEIGHT,
    };
  }

  if (type === 'storyboardPlanCreate') {
    return { width: 320, height: 260 };
  }

  if (type === 'storyboardDialogueCreate') {
    return { width: STORYBOARD_AUDIO_ITEM_WIDTH, height: 260 };
  }

  return {
    width: ASSET_CANVAS_SQUARE_NODE_SIZE,
    height: ASSET_CANVAS_SQUARE_NODE_SIZE,
  };
}

export function getCanvasNodeVisualCenter(
  node: CanvasNodeWithSize,
  fallbackSize: { width: number; height: number }
): { x: number; y: number } {
  const width = node.measured?.width ?? node.width ?? fallbackSize.width;
  const height = node.measured?.height ?? node.height ?? fallbackSize.height;
  return {
    x: node.position.x + width / 2,
    y: node.position.y + height / 2,
  };
}

/** 顶部偏置时，只用组顶部这段高度的中心，避免高组几何居中把标题顶出视野。 */
export const CANVAS_NODE_FOCUS_TOP_ALIGN_VISIBLE_HEIGHT = 480;

export type CanvasNodeFocusAlign = 'center' | 'start';

/**
 * 视口对焦点。align 默认 center，数值与 {@link getCanvasNodeVisualCenter} 一致。
 * start：组顶部可见高度中心。headerAllowance 仅历史组传入，避免普通卡片对焦偏上。
 */
export function getCanvasNodeFocusPoint(
  node: CanvasNodeWithSize,
  fallbackSize: { width: number; height: number },
  options?: { align?: CanvasNodeFocusAlign; headerAllowance?: number }
): { x: number; y: number } {
  const center = getCanvasNodeVisualCenter(node, fallbackSize);
  if ((options?.align ?? 'center') === 'center') {
    return center;
  }

  const height = node.measured?.height ?? node.height ?? fallbackSize.height;
  const visibleHeight = Math.min(height, CANVAS_NODE_FOCUS_TOP_ALIGN_VISIBLE_HEIGHT);
  return {
    x: center.x,
    y: node.position.y + visibleHeight / 2 - (options?.headerAllowance ?? 0),
  };
}
